import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, Search, Trash2, Users, X } from "lucide-react";
import { api } from "../../../../services/erpApi";
import { useToast } from "../../../../components/admin/ToastContext";
import { ODOO } from "./crmV2Helpers";

const EMPTY_FORM = { name: "", color: ODOO.primary, description: "", is_active: true };

const fieldStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: `1px solid ${ODOO.border}`,
  borderRadius: 3,
  padding: "8px 10px",
  font: "inherit",
  fontSize: 13,
};

const CrmV2GroupsModal = ({ open, groups = [], initialGroupId = null, onClose, onRefreshGroups }) => {
  const toast = useToast();
  const initialApplied = useRef(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [memberLeads, setMemberLeads] = useState([]);
  const [memberIds, setMemberIds] = useState(() => new Set());
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [busyLeadId, setBusyLeadId] = useState(null);
  const searchTimer = useRef(null);

  const selectedGroup = useMemo(
    () => groups.find((g) => String(g.id) === String(selectedId)) || null,
    [groups, selectedId]
  );

  const loadMembers = useCallback(async (groupId) => {
    if (!groupId) {
      setMemberLeads([]);
      setMemberIds(new Set());
      return;
    }
    setLoadingLeads(true);
    try {
      const rows = await api.crm.groups.leads(groupId, { limit: 1000 });
      const list = Array.isArray(rows) ? rows : [];
      setMemberLeads(list);
      setMemberIds(new Set(list.map((l) => l.id)));
    } catch (e) {
      toast(e.message || "Failed to load group members", "error");
      setMemberLeads([]);
      setMemberIds(new Set());
    }
    setLoadingLeads(false);
  }, [toast]);

  useEffect(() => {
    if (!open) return;
    setCreating(false);
    setEditing(false);
    setForm(EMPTY_FORM);
    setSearch("");
    setSearchResults([]);
    setBusyLeadId(null);
  }, [open]);

  useEffect(() => {
    if (!open) {
      initialApplied.current = false;
      return;
    }
    if (initialApplied.current) return;
    initialApplied.current = true;
    if (initialGroupId && groups.some((g) => String(g.id) === String(initialGroupId))) {
      setSelectedId(initialGroupId);
    }
  }, [open, initialGroupId, groups]);

  useEffect(() => {
    if (!open) return;
    if (!selectedId) {
      if (groups.length > 0) setSelectedId(groups[0].id);
      return;
    }
    if (!groups.some((g) => String(g.id) === String(selectedId))) {
      setSelectedId(groups[0]?.id ?? null);
    }
  }, [open, groups, selectedId]);

  useEffect(() => {
    if (!open || !selectedId || creating || editing) return;
    loadMembers(selectedId);
  }, [open, selectedId, creating, editing, loadMembers]);

  useEffect(() => {
    if (!open || creating || editing) return;
    clearTimeout(searchTimer.current);
    const term = search.trim();
    if (!term) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const rows = await api.crm.list({ search: term, limit: 25, page: 1 });
        setSearchResults(Array.isArray(rows) ? rows : []);
      } catch (e) {
        toast(e.message || "Search failed", "error");
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [open, search, creating, editing, toast]);

  if (!open) return null;

  const openCreate = () => {
    setCreating(true);
    setEditing(false);
    setForm(EMPTY_FORM);
  };

  const openEdit = () => {
    if (!selectedGroup) return;
    setCreating(false);
    setEditing(true);
    setForm({
      name: selectedGroup.name || "",
      color: selectedGroup.color || ODOO.primary,
      description: selectedGroup.description || "",
      is_active: selectedGroup.is_active !== false,
    });
  };

  const cancelForm = () => {
    setCreating(false);
    setEditing(false);
    setForm(EMPTY_FORM);
  };

  const saveGroup = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color || null,
        description: form.description.trim() || null,
        is_active: form.is_active,
      };
      if (creating) {
        const created = await api.crm.groups.create(payload);
        toast("Group created", "success");
        if (created?.id) setSelectedId(created.id);
      } else if (selectedGroup) {
        await api.crm.groups.update(selectedGroup.id, payload);
        toast("Group updated", "success");
      }
      setCreating(false);
      setEditing(false);
      setForm(EMPTY_FORM);
      await onRefreshGroups?.();
    } catch (e) {
      toast(e.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async () => {
    if (!selectedGroup || saving) return;
    if (!window.confirm(`Delete the group “${selectedGroup.name}”?`)) return;
    setSaving(true);
    try {
      await api.crm.groups.remove(selectedGroup.id);
      toast("Group deleted", "success");
      setSelectedId(null);
      setCreating(false);
      setEditing(false);
      await onRefreshGroups?.();
    } catch (e) {
      toast(e.message || "Delete failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleLead = async (lead) => {
    if (!selectedGroup || busyLeadId) return;
    const isMember = memberIds.has(lead.id);
    setBusyLeadId(lead.id);
    try {
      if (isMember) {
        await api.crm.groups.removeLead(selectedGroup.id, lead.id);
        setMemberIds((prev) => {
          const next = new Set(prev);
          next.delete(lead.id);
          return next;
        });
        setMemberLeads((prev) => prev.filter((l) => l.id !== lead.id));
      } else {
        await api.crm.groups.addLeads(selectedGroup.id, { lead_ids: [lead.id] });
        setMemberIds((prev) => new Set(prev).add(lead.id));
        setMemberLeads((prev) => [lead, ...prev]);
      }
      await onRefreshGroups?.();
    } catch (e) {
      toast(e.message || "Update failed", "error");
    } finally {
      setBusyLeadId(null);
    }
  };

  const showSearch = search.trim().length > 0;
  const rows = showSearch ? searchResults : memberLeads;

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1250, background: "rgba(30,41,59,.32)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(980px, 100%)", height: "min(680px, 100%)", background: "#fff", borderRadius: 5, boxShadow: "0 18px 45px rgba(0,0,0,.22)", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${ODOO.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong style={{ fontSize: 15, color: ODOO.text }}>Prospect groups</strong>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: "none", background: "transparent", cursor: "pointer", color: ODOO.muted, display: "inline-flex" }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          <div style={{ width: 280, minWidth: 280, borderRight: `1px solid ${ODOO.border}`, display: "flex", flexDirection: "column", background: "#fafbfc" }}>
            <div style={{ padding: 10, borderBottom: `1px solid ${ODOO.borderLight}` }}>
              <button
                type="button"
                onClick={openCreate}
                style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, border: `1px solid ${ODOO.primary}`, background: "#fff", color: ODOO.primary, padding: "7px 10px", font: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}
              >
                <Plus size={14} /> New group
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {groups.length === 0 ? (
                <div style={{ padding: 16, fontSize: 12.5, color: ODOO.muted }}>No groups yet.</div>
              ) : (
                groups.map((g) => {
                  const active = String(g.id) === String(selectedId) && !creating;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => { setSelectedId(g.id); setCreating(false); setEditing(false); setSearch(""); }}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, border: "none", borderBottom: `1px solid ${ODOO.borderLight}`, background: active ? ODOO.primarySoft : "transparent", boxShadow: active ? `inset 3px 0 0 ${ODOO.primary}` : "none", padding: "10px 12px", textAlign: "left", cursor: "pointer", font: "inherit" }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: g.color || ODOO.primary, flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: active ? 700 : 400, color: ODOO.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</span>
                      {g.is_default && <span style={{ fontSize: 9, fontWeight: 700, color: ODOO.primary, background: "#eef2f7", borderRadius: 8, padding: "1px 5px" }}>DEFAULT</span>}
                      <span style={{ fontSize: 11, color: ODOO.muted }}>{g.lead_count ?? 0}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {creating || editing ? (
              <div style={{ padding: 18, display: "grid", gap: 14, alignContent: "start" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: ODOO.text }}>{creating ? "New group" : "Edit group"}</div>
                <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>
                  Name *
                  <input autoFocus value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} style={{ ...fieldStyle, marginTop: 5 }} />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>
                  Color
                  <input type="color" value={form.color || ODOO.primary} onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))} style={{ ...fieldStyle, marginTop: 5, height: 38, padding: 4 }} />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>
                  Description
                  <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={3} style={{ ...fieldStyle, marginTop: 5, resize: "vertical" }} />
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: ODOO.text, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                  Active
                </label>
                <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
                  <button type="button" onClick={saveGroup} disabled={saving || !form.name.trim()} style={{ border: "none", background: ODOO.primary, color: "#fff", borderRadius: 3, padding: "8px 14px", font: "inherit", fontWeight: 600, cursor: saving || !form.name.trim() ? "not-allowed" : "pointer", opacity: saving || !form.name.trim() ? 0.6 : 1 }}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button type="button" onClick={cancelForm} disabled={saving} style={{ border: `1px solid ${ODOO.border}`, background: "#fff", borderRadius: 3, padding: "8px 14px", font: "inherit", cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : selectedGroup ? (
              <>
                <div style={{ padding: "14px 16px", borderBottom: `1px solid ${ODOO.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", background: selectedGroup.color || ODOO.primary }} />
                  <strong style={{ fontSize: 15, color: ODOO.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedGroup.name}</strong>
                  <button type="button" onClick={openEdit} title="Edit group" style={{ border: `1px solid ${ODOO.border}`, background: "#fff", color: ODOO.muted, borderRadius: 3, padding: "5px 8px", cursor: "pointer", display: "inline-flex" }}><Pencil size={14} /></button>
                  {!selectedGroup.is_default && (
                    <button type="button" onClick={deleteGroup} title="Delete group" style={{ border: `1px solid ${ODOO.border}`, background: "#fff", color: "#be123c", borderRadius: 3, padding: "5px 8px", cursor: "pointer", display: "inline-flex" }}><Trash2 size={14} /></button>
                  )}
                </div>
                <div style={{ padding: 12, borderBottom: `1px solid ${ODOO.borderLight}` }}>
                  <div style={{ display: "flex", alignItems: "center", border: `1px solid ${ODOO.border}`, borderRadius: 3, background: "#fff", padding: "0 10px", height: 34 }}>
                    <Search size={15} style={{ color: ODOO.muted }} />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search a prospect to add…" style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "6px 8px", background: "transparent", font: "inherit" }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12, color: ODOO.muted }}>
                    <Users size={13} /> {memberIds.size} prospect{memberIds.size === 1 ? "" : "s"} in this group
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {loadingLeads && rows.length === 0 ? (
                    <div style={{ padding: 20, fontSize: 13, color: ODOO.muted }}>Loading…</div>
                  ) : rows.length === 0 ? (
                    <div style={{ padding: 20, fontSize: 13, color: ODOO.muted }}>{showSearch ? "No prospect found." : "No prospect in this group yet. Use the search above to add some."}</div>
                  ) : (
                    rows.map((lead) => {
                      const checked = memberIds.has(lead.id);
                      const locked = !!selectedGroup.is_default && checked;
                      const otherGroups = !checked && Array.isArray(lead.group_names) && lead.group_names.length > 0
                        ? lead.group_names.join(", ")
                        : "";
                      return (
                        <label key={lead.id} title={locked ? "Un prospect ne peut pas être retiré du groupe par défaut. Ajoutez-le à un autre groupe pour le déplacer." : undefined} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderBottom: `1px solid ${ODOO.borderLight}`, cursor: locked ? "default" : busyLeadId ? "wait" : "pointer", fontSize: 13, color: ODOO.text }}>
                          <input type="checkbox" checked={checked} disabled={busyLeadId === lead.id || locked} onChange={() => toggleLead(lead)} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: "block", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.name || "—"}</span>
                            <span style={{ display: "block", fontSize: 11.5, color: ODOO.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {lead.company || lead.partner_name || lead.email_from || lead.phone || lead.mobile || "—"}
                              {otherGroups ? ` · ${otherGroups}` : ""}
                            </span>
                          </span>
                          {lead.stage_name && <span style={{ fontSize: 11, color: ODOO.muted }}>{lead.stage_name}</span>}
                        </label>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <div style={{ padding: 24, fontSize: 13, color: ODOO.muted }}>Select a group or create a new one.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmV2GroupsModal;
